<script setup lang="tsx">
import type { UForm } from "#components";
import type { FormError, FormSubmitEvent } from "#ui/types";
import type { Schema } from "zod";

const props = defineProps<{
  email?: string;
}>();

const authStore = useUserStore();
const { t } = useI18n();
const isRequestInProgress = ref(false);
const hideModalContent = ref(false);
const formRef = ref<InstanceType<typeof UForm>>();

function onDismiss() {
  useModal().close();
}

const state = reactive({
  email: props.email ?? undefined,
});

const onValidate = (state: any): FormError[] => {
  const errors = [];

  if (state.email) {
    if (!validateEmail(state.email)) {
      errors.push({ path: "email", message: t("error.invalid_format") });
    }
  } else {
    errors.push({ path: "email", message: t("error.mandatory") });
  }

  return errors;
};

async function onSubmit(formData: FormSubmitEvent<Schema>) {
  resetMessageBoxProps();
  isRequestInProgress.value = true;

  const { success, errorMessage } = await authStore.triggerPasswordReset(
    formData.data.email
  );

  if (success) {
    messageBoxProperties.type = "success";
    messageBoxProperties.label = "login.modal.forgot_password.success"!;
    hideModalContent.value = true;
  } else {
    messageBoxProperties.type = "error";
    messageBoxProperties.label = errorMessage!;
  }

  isRequestInProgress.value = false;
}

// MESSAGE BOX
const messageBoxProperties = reactive({
  type: "",
  label: "",
});

function resetMessageBoxProps() {
  messageBoxProperties.label = messageBoxProperties.type = "";
}

const messageBoxLabelComputed = computed(() => messageBoxProperties.label);
</script>

<template>
  <CommonGenericModalContainer>
    <template #header>
      <CommonGenericModalHeader
        title-key="login.modal.forgot_password.title"
        :on-dismiss="onDismiss"
      />
    </template>

    <div class="flex flex-col gap-4">
      <div
        v-if="messageBoxLabelComputed !== ''"
        :class="
          messageBoxProperties.type == 'success'
            ? 'bg-primary-500 dark:bg-primary-400 w-full relative overflow-hidden rounded-md p-4 text-white dark:text-gray-900'
            : 'bg-red-500 dark:bg-red-400 w-full relative overflow-hidden rounded-md p-4 text-white dark:text-gray-900'
        "
      >
        <div class="flex gap-2 items-center">
          <UIcon
            :name="
              messageBoxProperties.type == 'success'
                ? 'i-heroicons-check-circle-16-solid'
                : 'i-heroicons-information-circle-20-solid'
            "
            class="w-5 h-5"
          />
          <p class="flex-1 text-sm font-medium">
            <i18n-t tag="span" :keypath="messageBoxLabelComputed">
              <template v-slot:br><br /></template>
            </i18n-t>
          </p>
        </div>
      </div>

      <p v-if="!hideModalContent">
        {{ $t("login.modal.forgot_password.message") }}
      </p>

      <UForm
        v-if="!hideModalContent"
        ref="formRef"
        :state="state"
        class="space-y-4"
        :validate="onValidate"
        :validateOn="['input', 'change', 'submit']"
        @submit="onSubmit"
        novalidate
      >
        <UFormGroup :label="t('common.email')" name="email">
          <UInput v-model="state.email" type="email" />
        </UFormGroup>
      </UForm>
    </div>

    <template v-if="!hideModalContent" #footer>
      <CommonGenericModalFooter>
        <UButton
          variant="ghost"
          :label="$t('common.cancel')"
          :disabled="isRequestInProgress"
          :block="$device.isMobile"
          @click="onDismiss"
        />

        <UButton
          :label="$t('common.submit')"
          class="transition-all"
          :loading="isRequestInProgress"
          :block="$device.isMobile"
          @click="formRef?.submit()"
        />
      </CommonGenericModalFooter>
    </template>
  </CommonGenericModalContainer>
</template>
